import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      // Common
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      warning: 'Warning',
      info: 'Information',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      view: 'View',
      search: 'Search',
      filter: 'Filter',
      sort: 'Sort',
      clear: 'Clear',
      submit: 'Submit',
      confirm: 'Confirm',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      close: 'Close',
      open: 'Open',
      yes: 'Yes',
      no: 'No',
      
      // Navigation
      home: 'Home',
      products: 'Products',
      cart: 'Cart',
      orders: 'Orders',
      profile: 'Profile',
      settings: 'Settings',
      support: 'Support',
      dashboard: 'Dashboard',
      logout: 'Logout',
      
      // Auth
      login: 'Login',
      register: 'Register',
      signIn: 'Sign In',
      signUp: 'Sign Up',
      signOut: 'Sign Out',
      forgotPassword: 'Forgot Password?',
      resetPassword: 'Reset Password',
      changePassword: 'Change Password',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      phone: 'Phone Number',
      otp: 'OTP',
      sendOtp: 'Send OTP',
      verifyOtp: 'Verify OTP',
      resendOtp: 'Resend OTP',
      
      // Products
      productName: 'Product Name',
      price: 'Price',
      description: 'Description',
      specifications: 'Specifications',
      reviews: 'Reviews',
      rating: 'Rating',
      inStock: 'In Stock',
      outOfStock: 'Out of Stock',
      addToCart: 'Add to Cart',
      buyNow: 'Buy Now',
      addToWishlist: 'Add to Wishlist',
      removeFromWishlist: 'Remove from Wishlist',
      category: 'Category',
      brand: 'Brand',
      warranty: 'Warranty',
      capacity: 'Capacity',
      solarInput: 'Solar Input',
      outputs: 'Outputs',
      weight: 'Weight',
      dimensions: 'Dimensions',
      
      // Cart & Checkout
      shoppingCart: 'Shopping Cart',
      cartEmpty: 'Your cart is empty',
      quantity: 'Quantity',
      subtotal: 'Subtotal',
      tax: 'Tax',
      shipping: 'Shipping',
      discount: 'Discount',
      total: 'Total',
      checkout: 'Checkout',
      proceedToCheckout: 'Proceed to Checkout',
      shippingAddress: 'Shipping Address',
      billingAddress: 'Billing Address',
      paymentMethod: 'Payment Method',
      deliveryOptions: 'Delivery Options',
      installationRequired: 'Installation Required',
      
      // Orders
      orderHistory: 'Order History',
      orderDetails: 'Order Details',
      orderNumber: 'Order Number',
      orderDate: 'Order Date',
      orderStatus: 'Order Status',
      trackOrder: 'Track Order',
      cancelOrder: 'Cancel Order',
      returnOrder: 'Return Order',
      reorder: 'Reorder',
      
      // Order Statuses
      pending: 'Pending',
      confirmed: 'Confirmed',
      processing: 'Processing',
      packed: 'Packed',
      shipped: 'Shipped',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      returned: 'Returned',
      refunded: 'Refunded',
      
      // Profile
      personalInfo: 'Personal Information',
      contactInfo: 'Contact Information',
      addresses: 'Addresses',
      paymentMethods: 'Payment Methods',
      notifications: 'Notifications',
      preferences: 'Preferences',
      language: 'Language',
      currency: 'Currency',
      timezone: 'Timezone',
      
      // Solar specific
      solarEnergy: 'Solar Energy',
      solarPower: 'Solar Power',
      solarPanel: 'Solar Panel',
      solarLight: 'Solar Light',
      solarPowerBank: 'Solar Power Bank',
      solarPump: 'Solar Pump',
      energySavings: 'Energy Savings',
      carbonReduction: 'Carbon Reduction',
      cleanEnergy: 'Clean Energy',
      sustainablePower: 'Sustainable Power',
      offGrid: 'Off-Grid',
      payg: 'Pay-As-You-Go',
      
      // Partner specific
      partnerDashboard: 'Partner Dashboard',
      productManagement: 'Product Management',
      orderManagement: 'Order Management',
      inventoryManagement: 'Inventory Management',
      financeAndPayouts: 'Finance & Payouts',
      uploadProduct: 'Upload Product',
      consignmentMode: 'Consignment Mode',
      dropShipping: 'Drop Shipping',
      commissionRate: 'Commission Rate',
      payoutSchedule: 'Payout Schedule',
      
      // Admin specific
      adminDashboard: 'Admin Dashboard',
      userManagement: 'User Management',
      partnerManagement: 'Partner Management',
      productApproval: 'Product Approval',
      catalogManagement: 'Catalog Management',
      systemSettings: 'System Settings',
      analytics: 'Analytics',
      reports: 'Reports',
      
      // Technician specific
      technicianDashboard: 'Technician Dashboard',
      jobList: 'Job List',
      serviceHistory: 'Service History',
      installation: 'Installation',
      maintenance: 'Maintenance',
      jobStatus: 'Job Status',
      customerSignature: 'Customer Signature',
      
      // Messages
      loginSuccess: 'Login successful!',
      loginError: 'Login failed. Please try again.',
      registerSuccess: 'Registration successful!',
      registerError: 'Registration failed. Please try again.',
      otpSent: 'OTP sent to your phone!',
      otpError: 'Failed to send OTP. Please try again.',
      productAdded: 'Product added to cart!',
      productRemoved: 'Product removed from cart!',
      orderPlaced: 'Order placed successfully!',
      orderCancelled: 'Order cancelled successfully!',
      profileUpdated: 'Profile updated successfully!',
      passwordChanged: 'Password changed successfully!',
      
      // Placeholders
      searchPlaceholder: 'Search for solar products...',
      emailPlaceholder: 'Enter your email address',
      phonePlaceholder: 'Enter your phone number',
      passwordPlaceholder: 'Enter your password',
      otpPlaceholder: 'Enter 6-digit OTP',
      
      // Buttons
      shopNow: 'Shop Now',
      learnMore: 'Learn More',
      getStarted: 'Get Started',
      contactUs: 'Contact Us',
      callNow: 'Call Now',
      viewAll: 'View All',
      showMore: 'Show More',
      showLess: 'Show Less',
      
      // Footer
      aboutUs: 'About Us',
      privacyPolicy: 'Privacy Policy',
      termsOfService: 'Terms of Service',
      customerService: 'Customer Service',
      helpCenter: 'Help Center',
      contactInfo: 'Contact Information',
      followUs: 'Follow Us',
      newsletter: 'Newsletter',
      subscribeNewsletter: 'Subscribe to Newsletter',
    }
  },
  sw: {
    translation: {
      // Common Swahili translations
      loading: 'Inapakia...',
      error: 'Hitilafu',
      success: 'Mafanikio',
      warning: 'Onyo',
      info: 'Taarifa',
      save: 'Hifadhi',
      cancel: 'Ghairi',
      delete: 'Futa',
      edit: 'Hariri',
      view: 'Ona',
      search: 'Tafuta',
      filter: 'Chuja',
      sort: 'Panga',
      clear: 'Futa',
      submit: 'Wasilisha',
      confirm: 'Thibitisha',
      back: 'Rudi',
      next: 'Ifuatayo',
      previous: 'Iliyotangulia',
      close: 'Funga',
      open: 'Fungua',
      yes: 'Ndiyo',
      no: 'Hapana',
      
      // Navigation
      home: 'Nyumbani',
      products: 'Bidhaa',
      cart: 'Kikapu',
      orders: 'Maagizo',
      profile: 'Wasifu',
      settings: 'Mipangilio',
      support: 'Msaada',
      dashboard: 'Dashibodi',
      logout: 'Toka',
      
      // Auth
      login: 'Ingia',
      register: 'Jisajili',
      signIn: 'Ingia',
      signUp: 'Jisajili',
      signOut: 'Toka',
      forgotPassword: 'Umesahau Nywila?',
      resetPassword: 'Weka Nywila Mpya',
      changePassword: 'Badilisha Nywila',
      email: 'Barua Pepe',
      password: 'Nywila',
      confirmPassword: 'Thibitisha Nywila',
      phone: 'Nambari ya Simu',
      otp: 'Nambari ya Uthibitisho',
      sendOtp: 'Tuma Nambari ya Uthibitisho',
      verifyOtp: 'Thibitisha Nambari',
      resendOtp: 'Tuma Tena',
      
      // Products
      productName: 'Jina la Bidhaa',
      price: 'Bei',
      description: 'Maelezo',
      specifications: 'Vipimo',
      reviews: 'Maoni',
      rating: 'Kiwango',
      inStock: 'Inapatikana',
      outOfStock: 'Haipatikani',
      addToCart: 'Weka Kikupuni',
      buyNow: 'Nunua Sasa',
      addToWishlist: 'Weka Orodha ya Matakwa',
      removeFromWishlist: 'Ondoa Orodha ya Matakwa',
      category: 'Jamii',
      brand: 'Chapa',
      warranty: 'Dhamana',
      capacity: 'Uwezo',
      solarInput: 'Uingizaji wa Jua',
      outputs: 'Matokeo',
      weight: 'Uzito',
      dimensions: 'Vipimo',
      
      // Solar specific
      solarEnergy: 'Nishati ya Jua',
      solarPower: 'Umeme wa Jua',
      solarPanel: 'Paneli ya Jua',
      solarLight: 'Taa ya Jua',
      solarPowerBank: 'Benki ya Umeme wa Jua',
      solarPump: 'Pampu ya Jua',
      energySavings: 'Kuokoa Nishati',
      carbonReduction: 'Kupunguza Kaboni',
      cleanEnergy: 'Nishati Safi',
      sustainablePower: 'Umeme Endelevu',
      offGrid: 'Bila Mitandao',
      payg: 'Lipa-Unavyotumia',
      
      // Messages
      loginSuccess: 'Umeingia kikamilifu!',
      loginError: 'Kuingia kumeshindwa. Jaribu tena.',
      registerSuccess: 'Usajili umefanikiwa!',
      registerError: 'Usajili umeshindwa. Jaribu tena.',
      otpSent: 'Nambari ya uthibitisho imetumwa kwenye simu yako!',
      otpError: 'Imeshindwa kutuma nambari ya uthibitisho. Jaribu tena.',
      productAdded: 'Bidhaa imeongezwa kikupuni!',
      productRemoved: 'Bidhaa imeondolewa kikupuni!',
      orderPlaced: 'Agizo limewekwa kikamilifu!',
      orderCancelled: 'Agizo limeghairiwa kikamilifu!',
      profileUpdated: 'Wasifu umeboreshwa kikamilifu!',
      passwordChanged: 'Nywila imebadilishwa kikamilifu!',
      
      // Placeholders
      searchPlaceholder: 'Tafuta bidhaa za jua...',
      emailPlaceholder: 'Ingiza barua pepe yako',
      phonePlaceholder: 'Ingiza nambari yako ya simu',
      passwordPlaceholder: 'Ingiza nywila yako',
      otpPlaceholder: 'Ingiza nambari ya uthibitisho',
      
      // Buttons
      shopNow: 'Nunua Sasa',
      learnMore: 'Jifunze Zaidi',
      getStarted: 'Anza',
      contactUs: 'Wasiliana Nasi',
      callNow: 'Piga Simu Sasa',
      viewAll: 'Ona Yote',
      showMore: 'Onyesha Zaidi',
      showLess: 'Onyesha Kidogo',
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: process.env.NODE_ENV === 'development',
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },
    
    interpolation: {
      escapeValue: false,
    },
    
    react: {
      useSuspense: false,
    },
  });

export default i18n;